<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store.js';

const store = useStore();
const { t } = useI18n();
const account = computed(() => store.getters.getCurrentAccount);

// Sem o evento load neste tempo, a tela mostra o aviso de demora. Isso cobre
// só lentidão e falha de rede: frame bloqueado (X-Frame-Options/CSP) e página
// de erro do proxy (502/503) também disparam load. Por isso o link "Abrir em
// nova aba" fica sempre visível na barra acima do iframe.
const HELP_CENTER_LOAD_TIMEOUT_MS = 15000;

/**
 * Embed da Central de Ajuda da SiteUp dentro do dashboard via iframe.
 *
 * - URL externa: https://ajuda.siteup.com.br?embed=1
 * - O parametro `?embed=1` faz a Central esconder seu proprio header/footer
 *   pra nao duplicar o frame do dashboard
 * - Cookie `siteup_embed` eh setado pelo middleware do siteup-site na
 *   primeira request, persistindo o modo embed entre navegacoes do iframe
 * - `?theme=dark|light` repassa o tema atual do painel
 *
 * Ajustes futuros possiveis:
 * - Passar `?account_id=X` pra Central exibir info contextual da conta
 * - Ouvir cliques em "Falar com a gente" pra abrir conversa direto no Chatwoot
 */
const currentTheme = () =>
  typeof document !== 'undefined' &&
  document.documentElement.classList.contains('dark')
    ? 'dark'
    : 'light';

const buildUrl = ({ embed }) => {
  const baseUrl = 'https://ajuda.siteup.com.br';
  const params = new URLSearchParams();
  if (embed) params.set('embed', '1');
  params.set('theme', currentTheme());
  if (account.value?.id) {
    params.set('account_id', String(account.value.id));
  }
  return `${baseUrl}?${params.toString()}`;
};

const helpCenterUrl = computed(() => buildUrl({ embed: true }));
// Nova aba abre a Central completa, sem o modo embed.
const newTabUrl = computed(() => buildUrl({ embed: false }));

const loaded = ref(false);
const timedOut = ref(false);
let timeoutId;

function onFrameLoad() {
  loaded.value = true;
  timedOut.value = false;
  clearTimeout(timeoutId);
}

onMounted(() => {
  timeoutId = setTimeout(() => {
    if (!loaded.value) timedOut.value = true;
  }, HELP_CENTER_LOAD_TIMEOUT_MS);
});
onBeforeUnmount(() => clearTimeout(timeoutId));
</script>

<template>
  <div class="flex flex-col h-full w-full bg-n-background">
    <div
      class="flex shrink-0 items-center justify-end border-b border-n-weak px-2"
    >
      <a
        :href="newTabUrl"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="help-center-new-tab-bar"
        class="inline-flex min-h-11 items-center gap-1.5 rounded-lg px-3 text-sm text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-n-brand"
      >
        <span class="i-ph-arrow-square-out size-4" aria-hidden="true" />
        {{ t('HELP_CENTER.EMBED.OPEN_NEW_TAB') }}
      </a>
    </div>
    <div class="relative flex flex-col flex-1 min-h-0">
      <div
        v-if="!loaded && !timedOut"
        role="status"
        data-testid="help-center-loading"
        class="absolute inset-0 flex flex-col gap-3 p-6 bg-n-background"
      >
        <span class="sr-only">{{ t('HELP_CENTER.EMBED.LOADING') }}</span>
        <div class="h-8 w-1/3 rounded-lg bg-n-alpha-2 animate-pulse" />
        <div class="h-4 w-2/3 rounded bg-n-alpha-2 animate-pulse" />
        <div class="h-4 w-1/2 rounded bg-n-alpha-2 animate-pulse" />
        <div class="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div class="h-28 rounded-xl bg-n-alpha-2 animate-pulse" />
          <div class="h-28 rounded-xl bg-n-alpha-2 animate-pulse" />
          <div class="h-28 rounded-xl bg-n-alpha-2 animate-pulse" />
        </div>
      </div>
      <div
        v-if="timedOut"
        role="alert"
        data-testid="help-center-timeout"
        class="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center bg-n-background"
      >
        <span
          class="i-ph-lifebuoy size-8 text-n-slate-10"
          aria-hidden="true"
        />
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('HELP_CENTER.EMBED.TIMEOUT_TITLE') }}
        </p>
        <p class="text-sm text-n-slate-11 max-w-md">
          {{ t('HELP_CENTER.EMBED.TIMEOUT_DESCRIPTION') }}
        </p>
        <a
          :href="newTabUrl"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="help-center-new-tab"
          class="inline-flex min-h-11 items-center gap-2 rounded-xl bg-n-brand px-4 text-sm font-medium text-white hover:bg-n-brand/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
        >
          <span class="i-ph-arrow-square-out size-4" aria-hidden="true" />
          {{ t('HELP_CENTER.EMBED.OPEN_NEW_TAB') }}
        </a>
      </div>
      <iframe
        :src="helpCenterUrl"
        class="flex-1 w-full border-0"
        :title="t('HELP_CENTER.EMBED.FRAME_TITLE')"
        allow="clipboard-write; clipboard-read"
        referrerpolicy="strict-origin-when-cross-origin"
        loading="eager"
        data-testid="help-center-frame"
        @load="onFrameLoad"
      />
    </div>
  </div>
</template>
